import React, { forwardRef, MutableRefObject, ReactNode, useLayoutEffect } from 'react';
import classNames from 'classnames';
import Content, { ContentProps, SidebarContent } from '@components/Content';
import styles from './ContentContainer.module.scss';
import { useAuthContext } from '@hooks/use-auth-context';
import { useSelector } from 'react-redux';
import { selectNavBarState } from '@reducers/ui/navBar';
import { EmptyErrorBoundary } from '@views/ErrorBoundary';
import { RightSidebarResizer } from '@components/PanelResizer';

function Primary(
  { children, className, ...props }: ContentProps,
  ref: MutableRefObject<HTMLDivElement>,
) {
  const { status } = useSelector(selectNavBarState);

  useLayoutEffect(() => {
    ref?.current?.focus();
  }, []);

  return (
    <Content
      ref={ref}
      className={classNames(styles.primary, className, styles[`navBar-${status}`])}
      {...props}
    >
      {children}
    </Content>
  );
}

function Sidebar({ children, className }: { children?: ReactNode; className?: string }) {
  const { status } = useSelector(selectNavBarState);
  return (
    <>
      <RightSidebarResizer />
      <SidebarContent
        className={classNames(styles.sidebar, 'sidebar', className, styles[`navBar-${status}`])}
      >
        <EmptyErrorBoundary>{children}</EmptyErrorBoundary>
      </SidebarContent>
    </>
  );
}

type ContainerProps = React.HTMLProps<any> & { as?: React.ElementType<any> };

export default function Container(containerProps: ContainerProps) {
  const { user } = useAuthContext();
  const { status, transitioning } = useSelector(selectNavBarState);
  const Component = containerProps.as || 'div';
  const { className, children, ...props } = containerProps;

  return (
    <Component
      className={classNames(
        styles.content,
        className,
        !user && styles.unauthenticatedUser,
        styles[`navBar-${status}`],
        transitioning && styles.navBarTransitioning,
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

Container.Primary = forwardRef(Primary);
Container.Sidebar = Sidebar;
